import { useTrustAuthorityCopy } from "./trustAuthorityCatalog";
import { formatActiveNumber } from "../../i18n/formatting";
import { useEffect, useState } from "react";
import {
  activateManagedSigningIdentity,
  listManagedSigningIdentities,
  stageManagedSigningIdentity,
  type ManagedSigningIdentity,
} from "./keysTrustApi";

/** Only handles LAB publication signer readiness; never publishes a release itself. */
export function CollectorLabSignerReadiness() {
  const { t } = useTrustAuthorityCopy();
  const [identities, setIdentities] = useState<ManagedSigningIdentity[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function refresh() {
    setBusy(true);
    setError(null);
    try {
      const all = await listManagedSigningIdentities();
      setIdentities(all.filter((item) => item.purpose === "collector_release_signer"));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("error.readSigner"));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => { void refresh(); }, []);

  const active = identities.filter((item) => item.lifecycle === "active");
  const staged = identities.filter((item) => item.lifecycle === "staged");
  const ready = active.length === 1;
  const blocked = active.length > 1 || staged.length > 1;
  async function generate() {
    if (identities.length !== 0) return;
    setBusy(true);
    setError(null);
    try {
      await stageManagedSigningIdentity({
        operationId: crypto.randomUUID(),
        purpose: "collector_release_signer",
      });
      setMessage(t("signer.stagedMessage"));
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("error.stageSigner"));
    } finally {
      setBusy(false);
    }
  }
  async function activate() {
    if (active.length !== 0 || staged.length !== 1) return;
    setBusy(true);
    setError(null);
    try {
      await activateManagedSigningIdentity(staged[0]!.operation_id);
      setMessage(t("signer.activatedMessage"));
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("error.activateSigner"));
    } finally {
      setBusy(false);
    }
  }
  return <section className="collector-consent-actions" aria-label={t("signer.aria")}>
    <h3>{t("signer.title")}</h3>
    <p>{ready
      ? t("signer.ready")
      : t("signer.required")}</p>
    <p>{t("signer.counts", { active: formatActiveNumber(active.length), staged: formatActiveNumber(staged.length) })}</p>
    {identities.length === 0 && <button type="button" disabled={busy} onClick={() => void generate()}>
      {t("signer.generate")}
    </button>}
    {active.length === 0 && staged.length === 1 && <button type="button" disabled={busy} onClick={() => void activate()}>
      {t("signer.activate")}
    </button>}
    {blocked && <p role="alert">{t("signer.blocked")}</p>}
    <button type="button" disabled={busy} onClick={() => void refresh()}>{t("signer.refresh")}</button>
    {message && <p role="status">{message}</p>}
    {error && <p role="alert">{error}</p>}
  </section>;
}
